/**
 * Human-readable error messages for users
 * Translates technical error codes to understandable feedback
 */

interface HumanErrorMessage {
  title: string;
  description: string;
  action?: string;
  actionLabel?: string;
  actionUrl?: string;
}

const ERROR_MESSAGES: Record<string, HumanErrorMessage> = {
  // Technical issues - NOT user's fault
  email_field_not_found: {
    title: "Tekniskt fel",
    description: "Vi kunde inte hitta email-fältet på ansökningssidan.",
    action: "Detta är ett problem hos oss, inte du. Försök igen eller ansök manuellt.",
  },
  selector_mismatch: {
    title: "Tekniskt fel",
    description: "Vi kunde inte hitta ett fält på ansökningssidan.",
    action: "Detta är ett problem hos oss. Försök igen eller ansök manuellt.",
  },
  field_not_found: {
    title: "Tekniskt fel",
    description: "Formuläret kunde inte fyllas i automatiskt.",
    action: "Detta är ett problem hos oss. Försök igen eller ansök manuellt.",
  },
  
  // Profile/data issues - only show if data ACTUALLY missing
  missing_email: {
    title: "E-post saknas",
    description: "Din profil saknar e-postadress.",
    action: "Lägg till din e-post under inställningar.",
    actionLabel: "Gå till inställningar",
    actionUrl: "/settings",
  },
  missing_cv: {
    title: "CV saknas",
    description: "Du har inte laddat upp ett CV än.",
    action: "Ladda upp ditt CV för att använda snabbansökan.",
    actionLabel: "Ladda upp CV",
    actionUrl: "/settings?tab=cv",
  },
  missing_city: {
    title: "Stad saknas",
    description: "Din profil saknar stad.",
    action: "Lägg till din stad för att använda snabbansökan.",
    actionLabel: "Gå till inställningar",
    actionUrl: "/settings",
  },
  missing_name: {
    title: "Namn saknas",
    description: "Din profil saknar ditt namn.",
    action: "Fyll i ditt namn under inställningar.",
    actionLabel: "Gå till inställningar",
    actionUrl: "/settings",
  },
  missing_phone: {
    title: "Telefon saknas",
    description: "Vissa arbetsgivare kräver telefonnummer.",
    action: "Lägg till ditt telefonnummer för att öka chansen att lyckas.",
    actionLabel: "Lägg till telefon",
    actionUrl: "/settings",
  },

  // Platform issues
  platform_disabled: {
    title: "Plattformen stöds inte just nu",
    description: "Vi kan inte automatisera ansökan till denna arbetsgivare.",
    action: "Du kan ansöka manuellt genom att klicka på länken nedan.",
  },
  platform_captcha: {
    title: "Säkerhetskontroll (CAPTCHA) krävs",
    description: "Arbetsgivaren använder en säkerhetskontroll (t.ex. Lever/hCaptcha) som vi inte kan lösa automatiskt.",
    action: "Klicka 'Försök igen' → 'Öppna och skicka manuellt'. Öppna länken, lös CAPTCHA på sidan och skicka.",
  },
  captcha_blocked: {
    title: "Säkerhetskontroll (CAPTCHA) krävs",
    description: "Arbetsgivaren använder en säkerhetskontroll som vi inte kan lösa automatiskt.",
    action: "Öppna länken och skicka ansökan manuellt.",
  },
  platform_login_required: {
    title: "Inloggning krävs",
    description: "Du måste logga in på arbetsgivarens sida för att ansöka.",
    action: "Öppna sidan och logga in med ditt konto.",
  },
  platform_form_changed: {
    title: "Formuläret har ändrats",
    description: "Arbetsgivaren har uppdaterat sin ansökningssida.",
    action: "Försök igen eller ansök manuellt.",
  },
  dom_changed: {
    title: "Formuläret har ändrats",
    description: "Ansökningsformuläret ser annorlunda ut än väntat.",
    action: "Försök igen eller ansök manuellt.",
  },
  
  // Lever-specific (hCaptcha – kan inte automatiseras)
  lever_hcaptcha_present: {
    title: "Säkerhetskontroll (hCaptcha) krävs",
    description: "Lever använder hCaptcha. Vi kan inte lösa CAPTCHA automatiskt.",
    action: "Använd 'Försök igen' → 'Öppna och skicka manuellt'. Lös CAPTCHA på sidan och skicka.",
  },
  
  // Varbi-specific (Swedish municipalities)
  varbi_bankid_required: {
    title: "BankID krävs",
    description: "Denna arbetsgivare kräver inloggning med BankID.",
    action: "Öppna sidan och logga in med ditt BankID för att ansöka.",
  },
  varbi_login_required: {
    title: "Inloggning krävs",
    description: "Du måste logga in för att ansöka hos denna myndighet.",
    action: "Öppna sidan och logga in med ditt konto.",
  },

  // Network/technical issues
  network_error: {
    title: "Anslutningsproblem",
    description: "Kunde inte nå ansökningssidan just nu.",
    action: "Kontrollera din internetanslutning och försök igen.",
  },
  timeout: {
    title: "Sidan svarar inte",
    description: "Ansökningssidan tog för lång tid att svara.",
    action: "Försök igen om en stund eller ansök manuellt.",
  },
  infra_timeout_ttl: {
    title: "Sidan svarar inte",
    description: "Ansökningssidan tog för lång tid att svara.",
    action: "Vi försöker igen automatiskt eller så kan du ansöka manuellt.",
  },
  server_error: {
    title: "Serverfel",
    description: "Något gick fel på vår sida.",
    action: "Försök igen om några minuter.",
  },
  connection_failed: {
    title: "Anslutningsproblem",
    description: "Kunde inte nå ansökningssidan.",
    action: "Försök igen eller ansök manuellt.",
  },
  no_form: {
    title: "Ingen ansökningslänk hittades",
    description: "Vi kunde inte hitta en ansökningslänk för det här jobbet.",
    action: "Öppna jobbet och ansök manuellt.",
  },
  job_board: {
    title: "Jobbsajt kräver manuell ansökan",
    description: "Detta jobb kommer från en jobbsajt utan direkt ansökningsformulär.",
    action: "Öppna länken och ansök manuellt.",
  },
  platform_not_supported: {
    title: "Plattformen stöds inte",
    description: "Vi kan inte automatisera ansökan till denna arbetsgivare.",
    action: "Öppna länken och ansök manuellt.",
  },

  // Submission issues
  already_applied: {
    title: "Redan ansökt",
    description: "Du har redan skickat en ansökan till detta jobb.",
  },
  submission_failed: {
    title: "Kunde inte skicka",
    description: "Ansökan gick inte att skicka just nu.",
    action: "Försök igen eller ansök manuellt.",
  },

  // Rate limiting
  rate_limited: {
    title: "För många ansökningar",
    description: "Du har skickat många ansökningar nyligen.",
    action: "Vänta några minuter innan du försöker igen.",
  },

  // Generic fallback
  unknown: {
    title: "Något gick fel",
    description: "Vi kunde inte slutföra ansökan.",
    action: "Försök igen eller ansök manuellt.",
  },
};

/**
 * Get a human-readable error message for a given error code or message
 */
export function getHumanErrorMessage(errorCode: string | null | undefined): HumanErrorMessage {
  if (!errorCode) {
    return ERROR_MESSAGES.unknown;
  }

  const normalized = errorCode.toLowerCase().replace(/[-\s]/g, "_");

  // Direct match
  if (ERROR_MESSAGES[normalized]) {
    return ERROR_MESSAGES[normalized];
  }

  // Pattern matching for common error types
  
  // CRITICAL: Technical errors MUST be checked FIRST
  // These are NOT the user's fault - never blame the user for selector/field issues
  if (/selector.?mismatch|field.?not.?found|element.?not.?found/i.test(errorCode)) {
    return ERROR_MESSAGES.selector_mismatch;
  }
  if (/email.?field.?not.?found|field.?email.?not.?found/i.test(errorCode)) {
    return ERROR_MESSAGES.email_field_not_found;
  }
  
  if (/captcha|hcaptcha|recaptcha|turnstile/i.test(errorCode)) {
    return ERROR_MESSAGES.platform_captcha;
  }
  if (/bankid/i.test(errorCode)) {
    return ERROR_MESSAGES.varbi_bankid_required;
  }
  if (/login|sign.?in|auth/i.test(errorCode)) {
    return ERROR_MESSAGES.platform_login_required;
  }
  if (/network|fetch|connection|offline/i.test(errorCode)) {
    return ERROR_MESSAGES.network_error;
  }
  if (/timeout|timed.?out/i.test(errorCode)) {
    return ERROR_MESSAGES.timeout;
  }
  if (/rate.?limit|too.?many/i.test(errorCode)) {
    return ERROR_MESSAGES.rate_limited;
  }
  if (/disabled|blocked|unsupported/i.test(errorCode)) {
    return ERROR_MESSAGES.platform_disabled;
  }
  
  // IMPORTANT: Only match "missing_email" for ACTUAL missing profile data
  // NOT for technical errors like "email_field_not_found"
  if (/^missing.?email$|^no.?email$|profile.?missing.?email/i.test(errorCode)) {
    return ERROR_MESSAGES.missing_email;
  }
  if (/missing.?cv|no.?cv|cv.?required/i.test(errorCode)) {
    return ERROR_MESSAGES.missing_cv;
  }
  if (/missing.?name|no.?name/i.test(errorCode)) {
    return ERROR_MESSAGES.missing_name;
  }

  return ERROR_MESSAGES.unknown;
}

/**
 * Check if an error is user-actionable (they can do something about it)
 */
export function isUserActionableError(errorCode: string | null | undefined): boolean {
  const message = getHumanErrorMessage(errorCode);
  return !!message.action || !!message.actionUrl;
}

/**
 * Get the category of an error for analytics/logging
 */
export function getErrorCategory(errorCode: string | null | undefined): "profile" | "platform" | "network" | "unknown" {
  if (!errorCode) return "unknown";
  
  const normalized = errorCode.toLowerCase();
  
  if (/missing|profile|cv|email|name|phone/i.test(normalized)) {
    return "profile";
  }
  if (/platform|captcha|login|form/i.test(normalized)) {
    return "platform";
  }
  if (/network|timeout|connection|server/i.test(normalized)) {
    return "network";
  }
  
  return "unknown";
}
